import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { z } from 'zod';

// GET /api/v4/single_root/version (Api::V4::SingleRoot::VersionsController).
export const VERSION_PATH = '/api/v4/single_root/version';
// Waits between checks while a hint insists the build changed but web still serves the tab's:
// a deploy runs the new image on cable before CodeDeploy shifts web traffic, and a rollback
// leaves web on the old build for good.
export const RETRY_DELAYS_MS = [1, 2, 4, 8, 15].map((minutes) => minutes * 60_000);
// A tab hidden this long may have slept through a deploy.
export const VISIBLE_RECHECK_MS = 30 * 60_000;
// Every socket reconnects at once after a deploy, so cable hints spread their first check.
const MISMATCH_JITTER_MS = 60_000;
const DISMISSED_KEY = 'scout.singleRoot.dismissedAssetVersion';

/**
 * Why a check is due. `visibility`: the tab came back after a while. `mismatch`: something
 * reported a different build (the cable welcome). `chunk`: a lazy chunk failed to load, which is
 * what a deploy does to a stale tab. Only the last two retry while web still serves the tab's build.
 */
export type CheckReason = 'visibility' | 'mismatch' | 'chunk';
const STRENGTH: Record<CheckReason, number> = { visibility: 0, mismatch: 1, chunk: 2 };

export interface PendingCheck {
  reason: CheckReason;
  /** 0 for the first request, then the index into RETRY_DELAYS_MS plus one. */
  attempt: number;
}

export interface AvailableUpdate {
  version: string;
  dismissed: boolean;
}

// The tab's own build is fixed at boot, so the state records only what web last said and
// whether another question is due. `stopped` is final: the server said no (flag off, 401, 403).
export type DeployVersionState =
  | { kind: 'stopped' }
  | { kind: 'watching'; update: AvailableUpdate | null; check: PendingCheck | null };

export type DeployVersionEvent =
  | { type: 'hint'; reason: CheckReason }
  /** Web's answer; null stops checking. `dismissedVersion` is the one Later was pressed for. */
  | { type: 'confirmed'; version: string | null; dismissedVersion: string | null }
  | { type: 'failed' }
  | { type: 'dismiss' };

const nextAttempt = (check: PendingCheck | null): PendingCheck | null =>
  check && check.attempt < RETRY_DELAYS_MS.length ? { ...check, attempt: check.attempt + 1 } : null;

export const initialDeployVersionState = (bundle: string | null | undefined): DeployVersionState =>
  bundle ? { kind: 'watching', update: null, check: null } : { kind: 'stopped' };

export const deployVersionReducer =
  (bundle: string) =>
  (state: DeployVersionState, event: DeployVersionEvent): DeployVersionState => {
    if (state.kind === 'stopped') return state;
    const { check, update } = state;
    switch (event.type) {
      case 'hint':
        // A pending check keeps its timer unless a stronger reason arrives.
        if (check && STRENGTH[check.reason] >= STRENGTH[event.reason]) return state;
        return { ...state, check: { reason: event.reason, attempt: 0 } };
      case 'confirmed': {
        const { dismissedVersion, version } = event;
        if (version === null) return { kind: 'stopped' };
        if (version === bundle) {
          const retry = check?.reason === 'visibility' ? null : nextAttempt(check);
          return { kind: 'watching', update: null, check: retry };
        }
        const dismissed =
          dismissedVersion === version || (update?.version === version && update.dismissed);
        return { kind: 'watching', update: { version, dismissed }, check: null };
      }
      case 'failed':
        return { ...state, check: nextAttempt(check) };
      case 'dismiss':
        return update && !update.dismissed
          ? { ...state, update: { ...update, dismissed: true } }
          : state;
      default:
        return state;
    }
  };

export const checkDelayMs = ({ attempt, reason }: PendingCheck): number => {
  if (attempt > 0) return RETRY_DELAYS_MS[attempt - 1] ?? 0;
  return reason === 'mismatch' ? Math.random() * MISMATCH_JITTER_MS : 0;
};

/** Web's build, or null when the server stops the prompt. Throws on other failures (a retry). */
export type VersionFetcher = () => Promise<string | null>;

const versionSchema = z.object({ assetVersion: z.string().nullable() });

export const fetchAssetVersion =
  (fetchImpl: typeof fetch = async (...args) => fetch(...args)): VersionFetcher =>
  async () => {
    const response = await fetchImpl(VERSION_PATH, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    // Signed out or no longer allowed: stop asking, and never reload into a sign-in page.
    if (response.status === 401 || response.status === 403) return null;
    if (!response.ok) throw new Error(`Version check failed (HTTP ${response.status})`);
    return versionSchema.parse(await response.json()).assetVersion;
  };

const defaultFetchVersion = fetchAssetVersion();

// sessionStorage throws in some private modes; the dismissal then lasts until the next reload.
const readDismissed = (): string | null => {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
};

const storeDismissed = (version: string): void => {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, version);
  } catch {
    // See readDismissed.
  }
};

export interface DeployVersion {
  /** A newer build web serves, once confirmed. */
  update: AvailableUpdate | null;
  hint: (reason: CheckReason) => void;
  dismiss: () => void;
  /** Read at click time by the navigation policy, so it never goes stale in a closure. */
  isUpdateReady: () => boolean;
}

/**
 * Whether web serves a newer build than the one this tab booted with (`bundle`, from the
 * bootstrap; null when the prompt's flag is off, which leaves this inert). Checks when the tab
 * becomes visible after VISIBLE_RECHECK_MS and on each hint. It never reloads the page itself.
 */
export const useDeployVersion = (
  bundle: string | null | undefined,
  fetchVersion: VersionFetcher = defaultFetchVersion
): DeployVersion => {
  const reducer = useMemo(() => deployVersionReducer(bundle ?? ''), [bundle]);
  const [state, dispatch] = useReducer(reducer, bundle, initialDeployVersionState);
  const lastCheckedAt = useRef(0);
  const updateReady = useRef(false);
  const update = state.kind === 'watching' ? state.update : null;
  const check = state.kind === 'watching' ? state.check : null;

  useEffect(() => {
    updateReady.current = update !== null;
  }, [update]);

  useEffect(() => {
    if (!check) return undefined;
    const timer = window.setTimeout(() => {
      lastCheckedAt.current = Date.now();
      fetchVersion().then(
        (version) => dispatch({ type: 'confirmed', version, dismissedVersion: readDismissed() }),
        () => dispatch({ type: 'failed' })
      );
    }, checkDelayMs(check));
    return () => window.clearTimeout(timer);
  }, [check, fetchVersion]);

  useEffect(() => {
    if (!bundle) return undefined;
    lastCheckedAt.current = Date.now();
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheckedAt.current < VISIBLE_RECHECK_MS) return;
      dispatch({ type: 'hint', reason: 'visibility' });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [bundle]);

  const hint = useCallback((reason: CheckReason) => dispatch({ type: 'hint', reason }), []);
  const dismiss = useCallback(() => {
    if (update) storeDismissed(update.version);
    dispatch({ type: 'dismiss' });
  }, [update]);
  const isUpdateReady = useCallback(() => updateReady.current, []);

  return useMemo(
    () => ({ update, hint, dismiss, isUpdateReady }),
    [update, hint, dismiss, isUpdateReady]
  );
};

// A stale tab asks for chunk hashes the deploy removed (shakapacker:clean keeps one build), and
// Rspack names that failure ChunkLoadError.
export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'ChunkLoadError';

export const UPDATED_MESSAGE = 'Scout was updated. Reload to open this.';
